/**
 * 统一导航状态管理
 * 支持动态菜单配置和实时更新
 * @author 系统
 * @date 2024
 */

import { defineStore } from 'pinia'
import { SITE_NAME, SITE_LOGO } from '@/constants/site'
import { ref, computed } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import {
  allNavigations,
  getAffixTabs,
  convertToMenuFormat,
  convertToTabFormat
} from '@/config/navigation'
import MenuConfigService from '@/services/MenuConfigService'
import type { NavigationItem, NavigationConfig } from '@/types/navigation'
import { useUserStore } from '@/stores/user'

/**
 * 面包屑项接口
 */
export interface BreadcrumbItem {
  title: string
  path?: string
  icon?: string
}

/**
 * 菜单项接口（兼容现有组件）
 */
export interface MenuItem {
  id: string
  title: string
  path?: string
  icon?: string
  order?: number
  hidden?: boolean
  permissions?: string[]
  children?: MenuItem[]
}

/**
 * 标签页项接口（兼容现有组件）
 */
export interface TabItem {
  path: string
  title: string
  icon?: string
  affix?: boolean
}

export const useNavigationStore = defineStore('navigation', () => {
  // 响应式状态
  const sidebarCollapsed = ref(localStorage.getItem('sidebar-collapsed') === 'true')
  const title = ref(SITE_NAME)
  const logo = ref(SITE_LOGO)
  const breadcrumbItems = ref<BreadcrumbItem[]>([])

  // 动态导航数据状态
  const dynamicNavigations = ref<NavigationConfig[]>([])
  const isRefreshing = ref(false)
  const lastUpdateTime = ref(0)
  const navigationUpdateTrigger = ref(0) // 导航更新触发器

  // 获取用户store
  const userStore = useUserStore()

  /**
   * 检查用户是否有权限
   */
  const hasPermission = (requiredPermissions: string[]): boolean => {
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true
    }

    const userPermissions = userStore.permissions || []
    const userRoles = userStore.roles || []

    // 如果是管理员或有通配符权限，显示所有导航
    if (userRoles.includes('admin') || userRoles.includes('super_admin') || userPermissions.includes('*')) {
      return true
    }

    // 开发环境下，如果用户未登录，显示所有导航（方便开发调试）
    if (import.meta.env.DEV && (!userStore.isLoggedIn || userPermissions.length === 0)) {
      return true
    }

    // 检查用户是否有所需权限
    return requiredPermissions.some(permission => userPermissions.includes(permission))
  }

  /**
   * 过滤导航配置
   */
  const filterNavigationConfig = (config: NavigationConfig): NavigationConfig | null => {
    if (config.hidden || config.meta?.hideInMenu) {
      return null
    }
    // 检查模块权限
    if (!hasPermission(config.permissions || [])) {
      return null
    }

    // 过滤子项
    const filteredItems = config.items.filter(item => {
      if (item.hidden || item.meta?.hideInMenu) {
        return false
      }
      return hasPermission(item.permissions || [])
    })

    if (filteredItems.length === 0) {
      return null
    }

    return {
      ...config,
      items: filteredItems
    }
  }

  // 计算属性

  /**
   * 有权限的模块导航列表
   */
  const filteredNavigations = computed(() => {
    return dynamicNavigations.value
      .map(config => filterNavigationConfig(config))
      .filter(config => config !== null) as NavigationConfig[]
  })

  /**
   * 有权限的所有导航项（扁平化）
   */
  const allNavigationItems = computed(() => {
    return filteredNavigations.value.flatMap(config => config.items)
  })

  const isPathMatch = (routePath: string, currentPath: string): boolean => {
    if (routePath === currentPath) {
      return true
    }

    const routeSegments = routePath.split('/')
    const currentSegments = currentPath.split('/')

    if (routeSegments.length !== currentSegments.length) {
      return false
    }

    return routeSegments.every((segment, index) => {
      if (segment.startsWith(':')) {
        return true
      }
      return segment === currentSegments[index]
    })
  }

  const findNavigationByPath = (path: string): NavigationItem | undefined => {
    return allNavigationItems.value.find(item => isPathMatch(item.path, path))
  }

  const findNavigationById = (id: string): NavigationItem | undefined => {
    return allNavigationItems.value.find(item => item.id === id)
  }

  /**
   * 菜单数据（兼容现有菜单组件）
   */
  const menuData = computed((): MenuItem[] => {

    const result = filteredNavigations.value.map(module => {
      const firstItem = module.items?.[0]
      // 检查是否为单个导航项的模块（一级菜单）
      if (module.items && module.items.length === 1 && firstItem && !firstItem.children) {
        // 单个导航项，直接作为菜单项
        const item = firstItem
        return {
          id: item.id,
          title: item.title,
          path: item.path,
          icon: item.icon,
          order: item.order,
          permissions: item.permissions
        }
      } else {
        // 多个导航项或有子菜单，作为菜单组
        const menuGroup = {
          id: module.name,
          title: module.title,
          path: `/${module.name}`, // 为菜单组设置唯一路径
          icon: module.icon,
          order: module.order,
          children: module.items?.map(item => ({
            id: item.id,
            title: item.title,
            path: item.path,
            icon: item.icon,
            order: item.order,
            permissions: item.permissions
          })) || []
        }
        return menuGroup
      }
    }).sort((a, b) => (a.order || 999) - (b.order || 999))

    return result
  })

  /**
   * 固定标签页列表
   */
  const affixTabs = computed((): TabItem[] => {
    const allAffixTabs = getAffixTabs()

    const filteredTabs = allAffixTabs.filter(item => {
      // 过滤权限
      if (!item.permissions || item.permissions.length === 0) {
        return true
      }

      const userPermissions = userStore.permissions || []
      const userRoles = userStore.roles || []

      // 如果是管理员，显示所有导航
      if (userRoles.includes('admin') || userRoles.includes('super_admin')) {
        return true
      }

      // 开发环境下，如果用户未登录，显示所有导航（方便开发调试）
      if (import.meta.env.DEV && (!userStore.isLoggedIn || userPermissions.length === 0)) {
        return true
      }

      const hasPermission = item.permissions.some(permission =>
        userPermissions.includes(permission)
      )
      return hasPermission
    })

    return filteredTabs.map(item => convertToTabFormat(item))
  })

  /**
   * 默认激活的标签页路径
   */
  const defaultActiveTabPath = computed(() => {
    const firstAffixTab = affixTabs.value[0]
    return firstAffixTab ? firstAffixTab.path : '/home'
  })

  // Actions

  /**
   * 设置侧边栏折叠状态
   */
  const setSidebarCollapsed = (collapsed: boolean) => {
    sidebarCollapsed.value = collapsed
    localStorage.setItem('sidebar-collapsed', collapsed.toString())
  }

  /**
   * 切换侧边栏折叠状态
   */
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed.value)
  }

  /**
   * 设置系统标题
   */
  const setTitle = (newTitle: string) => {
    title.value = newTitle
  }

  /**
   * 设置系统Logo
   */
  const setLogo = (newLogo: string) => {
    logo.value = newLogo
  }

  /**
   * 根据导航配置构建面包屑层级
   */
  const buildBreadcrumbFromNavigation = (currentNavItem: NavigationItem): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = []

    // 递归构建父级面包屑
    const buildParentBreadcrumbs = (navItem: NavigationItem) => {
      if (navItem.meta?.parent) {
        // 查找父级导航项
        const parentNavItem = findNavigationById(navItem.meta.parent)
        if (parentNavItem) {
          buildParentBreadcrumbs(parentNavItem)
          breadcrumbs.push({
            title: parentNavItem.title,
            path: parentNavItem.path,
            icon: parentNavItem.icon
          })
        }
      }
    }

    // 构建父级面包屑
    buildParentBreadcrumbs(currentNavItem)

    // 添加当前页面（最后一项不需要链接）
    breadcrumbs.push({
      title: currentNavItem.title,
      path: undefined,
      icon: currentNavItem.icon
    })

    return breadcrumbs
  }

  /**
   * 更新面包屑导航
   */
  const updateBreadcrumb = (route: RouteLocationNormalized) => {
    let breadcrumbs: BreadcrumbItem[] = []

    // 首先尝试从导航配置中查找当前页面
    const currentNavItem = findNavigationByPath(route.path)

    if (currentNavItem && currentNavItem.meta?.parent) {
      // 如果找到导航项且有父级关系，使用导航配置构建面包屑
      breadcrumbs = buildBreadcrumbFromNavigation(currentNavItem)
    } else {
      // 否则使用路由的 matched 信息来构建面包屑
      const matched = route.matched.filter(item => {
        return item.meta?.title &&
               item.path !== '/' &&
               !item.meta?.hideInBreadcrumb // 过滤掉设置了 hideInBreadcrumb 的路由
      })

      // 遍历匹配的路由，构建层级面包屑
      matched.forEach((item, index) => {
        const isLast = index === matched.length - 1
        const meta = item.meta

        // 跳过根路径和重定向路径
        if (item.path === '/' || !meta?.title) {
          return
        }

        // 从动态导航配置中查找对应的图标
        let iconFromNavigation = meta.icon as string

        // 在动态导航配置中查找匹配的路径，获取最新的图标
        const navigationItem = allNavigationItems.value.find(navItem =>
          navItem.path === item.path ||
          (navItem.path && item.path.startsWith(navItem.path))
        )

        if (navigationItem && navigationItem.icon) {
          iconFromNavigation = navigationItem.icon
        }

        breadcrumbs.push({
          title: meta.title as string,
          path: isLast ? undefined : item.path, // 最后一项不需要链接
          icon: iconFromNavigation
        })
      })

      // 如果没有匹配到路由信息，使用导航配置作为兜底
      if (breadcrumbs.length === 0 && currentNavItem) {
        breadcrumbs.push({
          title: currentNavItem.title,
          path: undefined,
          icon: currentNavItem.icon
        })
      }
    }

    breadcrumbItems.value = breadcrumbs
  }

  /**
   * 根据路径获取导航项信息
   */
  const getNavigationByPath = (path: string): NavigationItem | undefined => {
    return findNavigationByPath(path)
  }

  /**
   * 获取所有可用的导航路径
   */
  const getAllAvailablePaths = (): string[] => {
    return allNavigationItems.value
      .map(item => item.path)
      .filter(path => path) as string[]
  }

  /**
   * 更新模块排序
   * @param moduleId 模块ID
   * @param newOrder 新的排序值
   */
  const updateModuleOrder = async (moduleId: string, newOrder: number): Promise<void> => {
    const module = dynamicNavigations.value.find(nav => nav.title === moduleId)
    if (module) {
      // 先触发动画（记录当前位置）
      triggerSidebarAnimation()

      // 等待一小段时间确保位置记录完成
      await new Promise(resolve => setTimeout(resolve, 10))

      module.order = newOrder
      // 重新排序导航数组
      dynamicNavigations.value.sort((a, b) => (a.order || 999) - (b.order || 999))
    }
  }

  /**
   * 触发侧边栏菜单动画
   * 通知侧边栏组件执行动画效果
   */
  const triggerSidebarAnimation = (): void => {
    // 通过事件总线或直接调用侧边栏组件的动画方法
    // 这里使用自定义事件来通知侧边栏组件
    const event = new CustomEvent('sidebar-animation-trigger', {
      detail: { timestamp: Date.now() }
    })
    window.dispatchEvent(event)
  }

  /**
   * 刷新导航配置
   * 重新获取权限数据并更新导航配置
   * @param forceRefreshPermissions 是否强制刷新权限缓存，默认为false
   */
  const refreshNavigation = async (forceRefreshPermissions = false): Promise<void> => {
    try {

      const userStore = useUserStore()

      // 如果需要强制刷新权限，先刷新权限缓存
      if (forceRefreshPermissions) {
        await userStore.refreshPermissions()
      }

      // 获取用户权限（优先使用 permissions 字符串数组，兼容后端登录接口格式）
      const permissionCodes = userStore.permissions

      if (!permissionCodes || permissionCodes.length === 0) {
        console.warn('⚠️ 用户权限为空，返回空导航')
        dynamicNavigations.value = []
        navigationUpdateTrigger.value++
        return
      }

      // 使用权限代码字符串数组过滤本地导航配置
      const newNavigationConfig = await MenuConfigService.getNavigationConfig(permissionCodes)

      if (newNavigationConfig) {
        dynamicNavigations.value = newNavigationConfig

        // 触发导航更新事件
        navigationUpdateTrigger.value++
      } else {
        console.warn('⚠️ 获取导航配置失败')
        dynamicNavigations.value = []
      }

    } catch (error) {
      console.error('❌ 刷新导航配置失败:', error)
      dynamicNavigations.value = []
    }
  }

  /**
   * 重置导航配置为默认值
   */
  const resetNavigation = (): void => {
    dynamicNavigations.value = [...allNavigations]
  }

  /**
   * 设置面包屑
   */
  const setBreadcrumb = (items: BreadcrumbItem[]) => {
    breadcrumbItems.value = items
  }

  /**
   * 初始化导航配置
   */
  const initNavigation = async () => {
    if (dynamicNavigations.value.length === 0) {
      await refreshNavigation()
    }
  }

  /**
   * 强制刷新导航配置
   */
  const forceRefreshNavigation = async () => {
    isRefreshing.value = false // 重置状态
    await refreshNavigation()
  }

  /**
   * 获取导航更新时间
   */
  const getLastUpdateTime = () => lastUpdateTime.value

  return {
    // 状态
    sidebarCollapsed,
    title,
    logo,
    breadcrumbItems,
    isRefreshing,
    lastUpdateTime,

    // 计算属性
    filteredNavigations,
    allNavigationItems,
    affixTabs,
    menuData,
    defaultActiveTabPath,

    // 方法
    toggleSidebar,
    setSidebarCollapsed,
    setTitle,
    setLogo,
    updateBreadcrumb,
    setBreadcrumb,
    getNavigationByPath,
    getAllAvailablePaths,
    updateModuleOrder,
    triggerSidebarAnimation,
    refreshNavigation,
    resetNavigation,
    initNavigation,
    forceRefreshNavigation,
    getLastUpdateTime,
    hasPermission,
    findNavigationByPath,
    findNavigationById,
    convertToMenuFormat,
    convertToTabFormat
  }
})
